// The Horoscope (docs/01-Product/Horoscope.md), on the home half of the sky
// at /horoscope, or /archive/{date}/horoscope for a past night, in moonlight
// over the home's sky redrawn as a planisphere (SceneHost turns the chart on):
// a top bar with Back and "Nightly Horoscope", the night's full date on a
// hairline, then the three signs side by side (sun, moon, rising), each
// chosen from the night's constellations in the picker, and below them the
// column: an invitation and Generate Horoscope until it is written, then the
// text. While it is written the divining takes the whole window and ends on
// Reveal Horoscope. Locked until three constellations are written that night.
// A past night opens only to read a horoscope written on that night; without
// one, its address goes to tonight's home.
import { useCallback, useEffect, useRef, useState } from 'react';
import { navigate, nightPath, type HoroscopeRoute } from '../../app/router';
import type { Sign, SignConstellation } from '../../systems/api/types';
import {
  allPicked,
  horoscopeStore,
  leaveHoroscope,
  loadHoroscope,
  pickSign,
  revealHoroscope,
  SIGNS,
  writeHoroscope,
} from '../../systems/horoscope/horoscopeStore';
import { noteHoroscopeSeen } from '../../systems/horoscope/horoscopeNotice';
import { loadNight } from '../../systems/night/nightStore';
import { useStore } from '../../systems/store';
import { Button, IconButton } from '../components/Button';
import { Icon } from '../components/Icon';
import { useReducedMotion } from '../sky/reducedMotion';
import { WEEKDAY_SKIES, weekdayFromDate } from '../sky/weekdaySkies';
import { Divining } from './Divining';
import { HoroscopeText } from './HoroscopeText';
import { SignPicker } from './SignPicker';
import { SignSlot } from './SignSlot';
import './horoscope.css';

/** The divining's least length: long enough to be an occasion, short enough to wait through. */
export const WRITING_MIN_MS = 4500;
const WRITING_MIN_REDUCED_MS = 1500;
/** The divining fading away once revealed (horoscope.css). */
const CLOSING_MS = 450;

export function HoroscopeScreen({ route }: { route: HoroscopeRoute }) {
  const state = useStore(horoscopeStore);
  const reduced = useReducedMotion();
  const [picking, setPicking] = useState<Sign | null>(null);
  // The divining stays a moment after it ends, fading away over the screen.
  const [closing, setClosing] = useState(false);
  const [lastSpoken, setLastSpoken] = useState(false);
  const wasDivining = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    void loadNight(route.date);
    void loadHoroscope(route.date);
    const onVisible = () => document.visibilityState === 'visible' && void loadHoroscope(route.date);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      leaveHoroscope();
    };
  }, [route.date]);

  const data = state.data;
  const date = data?.date ?? route.date;
  const past = data ? data.date !== data.tonight : route.date !== null;
  const weekday = data?.weekday ?? (route.date ? weekdayFromDate(route.date) : null);
  const sky = WEEKDAY_SKIES[weekday ?? 'saturday'];
  const horoscope = data?.horoscope ?? null;
  const written = horoscope?.status === 'written' && horoscope.body ? horoscope : null;
  const locked = data !== null && data.made < data.unlockAt && !written;
  const byId = new Map<string, SignConstellation>((data?.constellations ?? []).map((c) => [c.id, c]));
  // A written horoscope keeps the signs it was written with, even ones no longer listed.
  const signOf = (sign: Sign): SignConstellation | null =>
    (horoscope && horoscope.status !== 'failed' ? horoscope.signs[sign] : null) ?? byId.get(state.picks[sign] ?? '') ?? null;
  const divining = (state.writing || state.awaitingReveal) && allSigns(signOf);
  const ready = allPicked(state.picks);

  // Only a horoscope written on its own night can be read later; otherwise home.
  useEffect(() => {
    if (data && data.date !== data.tonight && (!horoscope || horoscope.status === 'failed')) navigate('/', true);
  }, [data, horoscope]);

  // Tonight's constellations are seen: the home's Horoscope button drops its star.
  useEffect(() => {
    if (data && data.date === data.tonight) noteHoroscopeSeen(data.date, data.made);
  }, [data?.date, data?.tonight, data?.made]);

  // Ended (revealed, or the write failed): fade the divining away.
  useEffect(() => {
    if (divining) {
      wasDivining.current = true;
      setLastSpoken(state.awaitingReveal);
      return;
    }
    if (!wasDivining.current) return;
    wasDivining.current = false;
    if (!reduced) setClosing(true);
  }, [divining, state.awaitingReveal, reduced]);

  useEffect(() => {
    if (!closing) return;
    const id = setTimeout(() => setClosing(false), CLOSING_MS);
    return () => clearTimeout(id);
  }, [closing]);

  const closePicker = useCallback(() => setPicking(null), []);
  const write = () => void writeHoroscope(reduced ? WRITING_MIN_REDUCED_MS : WRITING_MIN_MS);
  const back = () => navigate(nightPath(past ? date : null));

  return (
    <div className={`horoscope-screen${divining ? ' is-divining' : ''}`} data-palette="moonlight">
      <div className="horoscope-content" inert={divining}>
        <header className="horoscope-header">
          <IconButton icon="back" label="Back" className="panel" onClick={back} />
          <h1 className="horoscope-title">Nightly Horoscope</h1>
        </header>
        <main className="home-main horoscope-main">
          <div className="home-column horoscope-column">
            {date && <p className="horoscope-date label-mono">{longDate(date)}</p>}
            {state.phase === 'error' && (
              <div className="panel home-notice">
                <p>{state.loadError}</p>
                <button type="button" className="btn btn-ghost btn-small" onClick={() => void loadHoroscope(route.date)}>
                  Try again
                </button>
              </div>
            )}
            {state.phase === 'loading' && <p className="quiet horoscope-loading">Reading the night's constellations…</p>}
            {data && locked && (
              <section className="panel horoscope-panel horoscope-locked">
                <Icon name="lock" className="horoscope-lock" />
                <p>The Horoscope opens after three constellations {past ? 'that night' : 'tonight'}.</p>
                <p className="horoscope-count mono">
                  {Math.min(data.made, data.unlockAt)} / {data.unlockAt}
                </p>
                <Button variant="ghost" onClick={back}>
                  {past ? 'Back to that night' : "Back to tonight's sky"}
                </Button>
              </section>
            )}
            {data && !locked && (
              <>
                <div className="sign-row">
                  {SIGNS.map((sign) => (
                    <SignSlot key={sign} sign={sign} constellation={signOf(sign)} sky={sky} fixed={written !== null || divining} onOpen={() => setPicking(sign)} />
                  ))}
                </div>
                <section className="panel horoscope-panel" aria-busy={state.writing}>
                  {written && !state.awaitingReveal ? (
                    <HoroscopeText horoscope={{ ...written, body: written.body! }} reveal={state.justWritten && !reduced} onRewrite={past ? undefined : write} />
                  ) : (
                    <div className="horoscope-invite">
                      <p className="horoscope-invite-text">
                        Choose a constellation for your sun, your moon and your rising sign. The same one may hold all three.
                      </p>
                      {state.writeError && <p className="horoscope-error">{state.writeError}</p>}
                      <Button variant="primary" className="horoscope-generate" disabled={!ready || divining} onClick={write}>
                        Generate Horoscope
                      </Button>
                      {!ready && <p className="horoscope-hint quiet">{remaining(state.picks)}</p>}
                    </div>
                  )}
                </section>
              </>
            )}
          </div>
          <footer className="home-footer quiet">© 2026 Paul Schulz &amp; Aaron Vanderbeek</footer>
        </main>
      </div>
      {picking && data && (
        <SignPicker
          sign={picking}
          constellations={data.constellations}
          picks={state.picks}
          sky={sky}
          onPick={(id) => {
            pickSign(picking, id);
            setPicking(null);
          }}
          onClose={closePicker}
        />
      )}
      {(divining || closing) && allSigns(signOf) && (
        <Divining
          signs={{ sun: signOf('sun')!, moon: signOf('moon')!, rising: signOf('rising')! }}
          spoken={divining ? state.awaitingReveal : lastSpoken}
          closing={!divining}
          onReveal={revealHoroscope}
        />
      )}
    </div>
  );
}

function allSigns(signOf: (s: Sign) => SignConstellation | null): boolean {
  return SIGNS.every((s) => signOf(s) !== null);
}

/** "Choose your moon and rising to begin." */
function remaining(picks: Partial<Record<Sign, string>>): string {
  const left = SIGNS.filter((s) => !picks[s]);
  const names = left.map((s) => (s === 'rising' ? 'rising sign' : s));
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
  return `Choose your ${list} to begin.`;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "Sunday, October 18 2026", as the Figma screen dates the night. */
function longDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const weekday = weekdayFromDate(date);
  return `${weekday[0]!.toUpperCase()}${weekday.slice(1)}, ${MONTHS[(m ?? 1) - 1]} ${d} ${y}`;
}
